import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AiAssistant,
  type AiAssistantSource,
  AsyncNotification,
  displaySourceLabel,
  DocumentPreviewPanel,
  toInAppAssistantLocation,
} from '@asset-metrix/portal-ui';

import { downloadAiAssistantDocument } from '../../api/portalGateway/aiAssistantDocument';
import { ROUTE_BASE_NAME } from '../../config';
import { useStore } from '../../store';

import { AiAssistantChromeProvider } from './AiAssistantChromeContext';
import { useChat } from './useChat';
import { useCurrentPage } from './useCurrentPage';
import { useDocumentContexts } from './useDocumentContexts';
import { useDocumentPreview } from './useDocumentPreview';

import styles from './AiAssistantLayout.module.scss';

type AiAssistantLayoutProps = {
  children: ReactNode;
  enabled: boolean;
};

export function getAiAssistantLayoutCssVars(
  reservedWidth: number,
  openWidth: number
): CSSProperties {
  return {
    '--portal-next-ai-assistant-reserved-width': `${reservedWidth}px`,
    '--page-sidebar-right-offset': `${openWidth}px`,
    '--page-sidebar-pinned-end-gap': `${Math.max(0, openWidth - reservedWidth)}px`,
  } as CSSProperties;
}

function EnabledAiAssistantLayout({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const closeAllPageSidebars = useStore((state) => state.closeAllPageSidebars);
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [reservedWidth, setReservedWidth] = useState(0);
  const [assistantWidth, setAssistantWidth] = useState(0);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const { closeDocumentPreview, openDocument, openDocumentPreview } =
    useDocumentPreview();
  const chromeValue = useMemo(
    () => ({ isOpen: open, isPinned: pinned }),
    [open, pinned]
  );
  const {
    activeChatId,
    chats,
    clearError,
    createChat,
    deleteChat,
    error,
    isLoading,
    isStreaming,
    selectChat,
    sendMessage,
  } = useChat(t('app.aiAssistant.newChat'));
  const currentPage = useCurrentPage();
  const previewDocuments = useMemo(
    () =>
      openDocument?.documentId
        ? [{ id: openDocument.documentId, label: openDocument.label }]
        : [],
    [openDocument?.documentId, openDocument?.label]
  );
  const availableDocuments = useDocumentContexts(previewDocuments);

  useEffect(() => {
    if (open && !isLoading && chats.length === 0) {
      createChat();
    }
  }, [chats.length, createChat, isLoading, open]);

  useEffect(() => {
    if (!error) {
      return;
    }
    AsyncNotification.show(
      { message: t('app.aiAssistant.requestError') },
      'error'
    );
    clearError();
  }, [clearError, error, t]);

  useEffect(() => {
    if (sourcesOpen || openDocument != null) {
      closeAllPageSidebars();
    }
  }, [closeAllPageSidebars, openDocument, sourcesOpen]);

  return (
    <AiAssistantChromeProvider value={chromeValue}>
      <div
        className={styles['layout']}
        style={getAiAssistantLayoutCssVars(reservedWidth, assistantWidth)}
      >
        <div className={styles['content']}>{children}</div>
        <AiAssistant
          activeChatId={activeChatId}
          chats={chats}
          availableDocuments={availableDocuments}
          composerDisabled={isLoading || isStreaming}
          currentPage={currentPage}
          onActiveChatChange={selectChat}
          onCloseDocument={closeDocumentPreview}
          onCreateChat={createChat}
          onDeleteChat={(chatId) => void deleteChat(chatId)}
          onDownloadSource={(source: AiAssistantSource) => {
            if (source.kind !== 'document' || source.documentId == null) {
              return;
            }
            void downloadAiAssistantDocument(
              source.documentId,
              displaySourceLabel(source.label)
            ).catch(() => {
              AsyncNotification.show(
                { message: t('app.aiAssistant.requestError') },
                'error'
              );
            });
          }}
          onNavigate={(to) => {
            closeDocumentPreview();
            // UI already drops origin / portal-page prefixes; basename finishes
            // router-relative paths (e.g. `/next/dashboard` → `/dashboard`).
            navigate(toInAppAssistantLocation(to, ROUTE_BASE_NAME));
          }}
          onOpenChange={setOpen}
          onOpenDocument={(document) => {
            closeAllPageSidebars();
            void openDocumentPreview(document).catch(() => {
              AsyncNotification.show(
                { message: t('app.aiAssistant.requestError') },
                'error'
              );
            });
          }}
          onOpenWidthChange={setAssistantWidth}
          onPinnedChange={setPinned}
          onReservedWidthChange={setReservedWidth}
          onSourcesOpenChange={setSourcesOpen}
          onSendMessage={sendMessage}
          open={open}
          openDocumentFile={openDocument?.file ?? null}
          pinned={pinned}
        />
        <DocumentPreviewPanel
          document={openDocument}
          onClose={closeDocumentPreview}
          rightOffset={assistantWidth}
        />
      </div>
    </AiAssistantChromeProvider>
  );
}

export function AiAssistantLayout({
  children,
  enabled,
}: AiAssistantLayoutProps) {
  return enabled ? (
    <EnabledAiAssistantLayout>{children}</EnabledAiAssistantLayout>
  ) : (
    children
  );
}
